import { useTranslation } from "react-i18next";

import type {
  CommercialParameters,
  CommercialValidationIssue,
} from "@/api/commercialCalibration";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { fieldIssues, numberOrNull, type CommercialForm } from "./form";

type ParameterField = keyof CommercialParameters;

/**
 * Parâmetros e incentivos da versão (EF-FUNC-012 v0.1 RF-04), com os nomes da
 * v0.3 RF-01: Consider Selection (C&S), antigo CCFlex, e Adopt Operation (A&O),
 * antigo Adopt. O erro vem da prévia do servidor e marca o campo.
 */
export function ParametersCard({
  parameters,
  issues,
  onChange,
}: {
  parameters: CommercialForm["parameters"];
  issues: CommercialValidationIssue[] | undefined;
  onChange: (field: ParameterField, value: number | null) => void;
}) {
  const { t } = useTranslation();
  const rows: { field: ParameterField; label: string; unit: string; hint?: string }[] = [
    { field: "commission_pct", label: t("Exed sales team commission"), unit: t("% of TCV") },
    { field: "tax_pct", label: t("Taxes"), unit: "%" },
    { field: "indirect_pct", label: t("Indirect costs"), unit: "%" },
    {
      field: "consider_selection_pct",
      label: "Consider Selection (C&S)",
      unit: t("% of software − commission"),
    },
    {
      field: "adopt_operation_pct",
      label: "Adopt Operation (A&O)",
      unit: t("% of software − commission"),
    },
    {
      field: "hyperscale_amount",
      label: "Hyperscale",
      unit: "R$",
      hint: t("informed contract by contract"),
    },
  ];

  return (
    <section className="flex flex-col">
      <h3 className="mb-1.5 font-mono text-[10.5px] uppercase tracking-[0.1em] text-[#5a6b86]">
        {t("Parameters")}
      </h3>
      {rows.map((row) => {
        const errors = fieldIssues(issues, row.field);
        const empty = parameters[row.field] === null;
        const invalid = empty || errors.length > 0;
        const id = `commercial-param-${row.field}`;
        return (
          <div key={row.field} className="flex flex-col gap-1 border-b border-dashed border-[#eef1f6] py-1.5">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 text-sm">
                <Label htmlFor={id}>{row.label}</Label>
                {row.hint ? (
                  <span className="ml-1.5 text-[11px] text-muted-foreground">({row.hint})</span>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Input
                  id={id}
                  type="number"
                  value={parameters[row.field] ?? ""}
                  aria-invalid={invalid}
                  onChange={(event) => onChange(row.field, numberOrNull(event.target.value))}
                  className={cn("h-8 w-28 text-right tabular-nums", invalid && "border-destructive")}
                />
                <span className="w-40 text-[11px] text-muted-foreground">{row.unit}</span>
              </div>
            </div>
            {empty ? (
              <p className="text-right text-[11px] text-destructive">{t("Fill in the value.")}</p>
            ) : null}
            {errors.map((error) => (
              <p key={error.code} className="text-right text-[11px] text-destructive">
                {issueMessage(error.code, row.field, t)}
              </p>
            ))}
          </div>
        );
      })}
    </section>
  );
}

/** A frase de cada erro de parâmetro. Os códigos são os do motor (spec do contrato). */
function issueMessage(
  code: CommercialValidationIssue["code"],
  field: ParameterField,
  t: (key: string) => string,
): string {
  if (code === "negative_hyperscale") return t("It cannot be negative.");
  if (field === "tax_pct") return t("From 0% to less than 100%.");
  return t("Between 0% and 100%.");
}
